import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Loader2, Save, RotateCcw, FileText, Eye, CheckCircle, Upload, Image, X } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import api from '../../lib/api';

const PageContentTab = () => {
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(null); // Track which section is uploading
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      setLoading(true);
      const res = await api.get('/page-content');
      setPages(res.data);
      if (res.data.length > 0 && !selectedPage) {
        loadPageContent(res.data[0].slug);
      }
    } catch (err) {
      console.error('Failed to fetch pages:', err);
      toast.error('Failed to load editable pages');
    } finally {
      setLoading(false);
    }
  };

  const loadPageContent = async (slug) => {
    try {
      setSelectedPage(slug);
      const res = await api.get(`/page-content/${slug}`);
      setContent(res.data);
    } catch (err) {
      console.error('Failed to load page content:', err);
      toast.error('Failed to load page content');
    }
  };

  const updateSection = (key, newContent) => {
    setContent(prev => ({
      ...prev,
      sections: prev.sections.map(s => 
        s.key === key ? { ...s, content: newContent } : s
      )
    }));
  };

  const handleImageUpload = async (sectionKey, file) => {
    if (!file) return;
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    // Validate file size (2MB max for persistent storage)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be under 2MB');
      return;
    }
    
    setUploadingImage(sectionKey);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await api.post('/site-media/upload/persistent', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (res.data.success) {
        // Use the data_url directly for immediate display, and store the persistent URL
        updateSection(sectionKey, res.data.data_url);
        toast.success('Image uploaded successfully!');
      }
    } catch (err) {
      console.error('Failed to upload image:', err);
      toast.error(err.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingImage(null);
    }
  };

  const handleRemoveImage = (sectionKey) => {
    updateSection(sectionKey, '');
  };

  const saveContent = async () => {
    try {
      setSaving(true);
      await api.put(`/page-content/${selectedPage}`, {
        sections: content.sections
      });
      toast.success('Page content saved successfully!');
      fetchPages(); // Refresh to update "customized" status
    } catch (err) {
      console.error('Failed to save:', err);
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const resetContent = async () => {
    if (!confirm('Reset this page to default content? This cannot be undone.')) return;
    
    try {
      setResetting(true);
      await api.post(`/page-content/${selectedPage}/reset`);
      toast.success('Page reset to defaults');
      loadPageContent(selectedPage);
      fetchPages();
    } catch (err) {
      console.error('Failed to reset:', err);
      toast.error('Failed to reset page');
    } finally {
      setResetting(false);
    }
  };

  const previewPage = () => {
    const urls = {
      'show-choir': '/show-choir',
      'primatotterinas': '/primatotterinas',
      'about': '/about',
      'contact': '/contact'
    };
    window.open(urls[selectedPage] || '/', '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-[#FF007F] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="page-content-tab">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Page Content Editor</h2>
          <p className="text-white/60 text-sm mt-1">
            Edit public page content without code changes
          </p>
        </div>
      </div>

      {/* Page Tabs */}
      <Tabs value={selectedPage} onValueChange={loadPageContent}>
        <TabsList className="bg-[#1a1a1a] border-[#333]">
          {pages.map(page => (
            <TabsTrigger 
              key={page.slug} 
              value={page.slug}
              className="data-[state=active]:bg-[#FF007F] data-[state=active]:text-white"
              data-testid={`page-tab-${page.slug}`}
            >
              <FileText className="w-4 h-4 mr-2" />
              {page.title}
              {page.is_customized && (
                <Badge className="ml-2 bg-green-500/20 text-green-400 text-xs">
                  Edited
                </Badge>
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        {pages.map(page => (
          <TabsContent key={page.slug} value={page.slug}>
            {content && selectedPage === page.slug && (
              <Card className="bg-[#111] border-[#333]">
                <CardHeader className="border-b border-[#333]">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-white flex items-center gap-2">
                      {content.page_title}
                      {content.is_default && (
                        <Badge className="bg-blue-500/20 text-blue-400">Default Content</Badge>
                      )}
                    </CardTitle>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={previewPage}
                        className="border-[#333] text-white hover:bg-white/10"
                        data-testid="preview-page"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Preview
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={resetContent}
                        disabled={resetting || content.is_default}
                        className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                        data-testid="reset-page"
                      >
                        {resetting ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <RotateCcw className="w-4 h-4 mr-1" />
                        )}
                        Reset
                      </Button>
                      <Button
                        onClick={saveContent}
                        disabled={saving}
                        className="bg-[#FF007F] hover:bg-[#D6006A]"
                        data-testid="save-page"
                      >
                        {saving ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-1" />
                        ) : (
                          <Save className="w-4 h-4 mr-1" />
                        )}
                        Save Changes
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-6">
                    {content.sections.map((section, idx) => (
                      <div key={section.key} className="space-y-2" data-testid={`section-${section.key}`}>
                        <label className="text-sm font-medium text-white/80">
                          {section.label}
                        </label>
                        {section.type === 'image' ? (
                          <div className="space-y-3">
                            {/* Image Preview */}
                            {section.content && (
                              <div className="relative inline-block">
                                <img 
                                  src={section.content} 
                                  alt={section.label}
                                  className="max-w-xs max-h-40 rounded-lg border border-[#333] object-cover"
                                />
                                <Button
                                  variant="destructive"
                                  size="icon"
                                  className="absolute -top-2 -right-2 h-6 w-6"
                                  onClick={() => handleRemoveImage(section.key)}
                                >
                                  <X className="h-3 w-3" />
                                </Button>
                              </div>
                            )}
                            
                            {/* Upload Button */}
                            <div className="flex items-center gap-3">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleImageUpload(section.key, e.target.files[0])}
                                className="hidden"
                                id={`upload-${section.key}`}
                              />
                              <label htmlFor={`upload-${section.key}`}>
                                <Button
                                  type="button"
                                  variant="outline"
                                  className="border-[#333] text-white hover:bg-white/10 cursor-pointer"
                                  disabled={uploadingImage === section.key}
                                  asChild
                                >
                                  <span>
                                    {uploadingImage === section.key ? (
                                      <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Uploading...
                                      </>
                                    ) : (
                                      <>
                                        <Upload className="w-4 h-4 mr-2" />
                                        {section.content ? 'Replace Image' : 'Upload Image'}
                                      </>
                                    )}
                                  </span>
                                </Button>
                              </label>
                              {!section.content && (
                                <span className="text-xs text-white/40">Max 2MB, JPG/PNG/WebP</span>
                              )}
                            </div>
                            
                            {/* Or paste URL */}
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-white/40">Or paste URL:</span>
                              <Input
                                value={section.content || ''}
                                onChange={(e) => updateSection(section.key, e.target.value)}
                                className="bg-[#0a0a0a] border-[#333] text-white text-sm flex-1"
                                placeholder="https://..."
                              />
                            </div>
                          </div>
                        ) : section.type === 'textarea' ? (
                          <Textarea
                            value={section.content}
                            onChange={(e) => updateSection(section.key, e.target.value)}
                            className="bg-[#0a0a0a] border-[#333] text-white min-h-[100px] resize-y"
                            placeholder={section.label}
                          />
                        ) : (
                          <Input
                            value={section.content}
                            onChange={(e) => updateSection(section.key, e.target.value)}
                            className="bg-[#0a0a0a] border-[#333] text-white"
                            placeholder={section.label}
                          />
                        )}
                        {section.key.includes('list') && (
                          <p className="text-xs text-white/40">
                            Enter each item on a new line
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Help Text */}
      <Card className="bg-[#0a0a0a] border-[#333]">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
            <div>
              <p className="text-white/80 text-sm font-medium">Changes are saved to the database</p>
              <p className="text-white/50 text-xs mt-1">
                Edits will appear on the live site immediately after saving. Use "Reset" to restore default content.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PageContentTab;
